import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import { AdfParser, AdfRepairStrategy, AdfSchemaValidator } from '@atlassian/adf-parser';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { ClientId, RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api';
import {
	InlineInputBoxAbortMessageCommand,
	ROVO_CHAT_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';

import type { ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';
import { fireInfiniteLoopDetectedAnalyticsEvent } from '../utils/analytics';

import {
	FinishAction,
	InitAction,
	PartialStreamParser,
	StreamAction,
} from './partial-stream-parser';


const SKIP_INFINITE_LOOP_ACTIONS = ['insertNodeAfter', 'insertNodeBefore'];

class ToolCallProcessor {
	private actionId: string;
	private accumulated: string = '';
	private parser: AdfParser | undefined;

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentLocation: ContentLocation,
		command: StreamingCommand,
	) {
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command, {
			/**
			 * Always hide the loading screen as staging area always shows Rovo conversation
			 * and will already have a loading indicator.
			 */
			hideLoadingScreen: this.contentLocation === 'staging-area',
		});
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		this.actionId = actionId;
		log('Initialized editor streaming action', { streamType: 'update', actionId, command });

		if(fg('platform_editor_ai_aifc_adf_parser_on_update')) {
			const schema = this.editorApi.core.sharedState.currentState()?.schema;
			if (schema) {
				this.parser = new AdfParser({
					schemaValidator: new AdfSchemaValidator(schema),
					repairStrategy: new AdfRepairStrategy(schema),
					config: {
						closeBrokenContent: fg('platform_editor_ai_improved_table_parsing'),
						lenientStringParsing: fg('platform_editor_ai_lenient_string_parsing'),
					},
				});
			}
		}
	}

	stream(adf: string, isFinished: boolean) {
		if (!adf) {
			return;
		}

		if (this.parser && fg('platform_editor_ai_aifc_adf_parser_on_update')) {
			this.parser.processChunk(adf, isFinished);
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(
				this.actionId,
				JSON.stringify(this.parser.getPartialDocument()) ?? '',
			);
		} else {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		}

		this.accumulated += adf;
		log('Streaming content', { actionId: this.actionId, adf });
	}

	finish() {
		this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
		log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });
		this.accumulated = '';
	}
}

function assertUnreachable(_x: never) {}

export class DocumentUpdateCommandProcessor {
	private parser: PartialStreamParser;
	private currentToolCall: ToolCallProcessor | undefined;
	private accumulated: string = '';
	private lastAction: string | null;
	private hasDetectedInfiniteLoop: boolean = false;

	constructor(
		private editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
		private contentLocation: ContentLocation,
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
	) {
		this.parser = new PartialStreamParser();
		this.lastAction = null;
	}

	private reset() {
		this.accumulated = '';
		this.lastAction = null;
		this.currentToolCall = undefined;
		this.hasDetectedInfiniteLoop = false;
		this.parser.reset();
	}

	private createToolCallProcessor(toolCall: InitAction) {
		const type = toolCall.name;
		const localId = toolCall.localId ?? '';

		let command: StreamingCommand;
		switch (type) {
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
			case 'removeNode':
				command = { type, localId };
				break;
			case 'appendNode':
			case 'appendNodeToEnd':
				command = { type };
				break;
			case 'wrap':
			case 'wrapNode':
				const wrapperType = toolCall.wrapperType;
				const wrapperAttrs = toolCall.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = { type, localId, wrapperType, wrapperAttrs };
				break;
			case 'setAttrs':
				const attrs = toolCall.attrs;
				if (!attrs) {
					return;
				}

				command = { type, localId, attrs };
				break;

			default:
				assertUnreachable(type);
				return;
		}

		return new ToolCallProcessor(this.editorApi, this.contentLocation, command);
	}

	streamChunk(chunk: string, isFinished: boolean): void {
		// The last message contains a copy of the full, accumulated tool call structure. We
		// don't want to reprocess this, so we just return instead.
		if (isFinished) {
			return;
		}

		if (this.accumulated.length === 0) {
			this.reset();
			this.editorApi.aiStreamingOrchestrator?.actions.startSession();
		}

		this.accumulated += chunk;
		return this.stream(chunk, isFinished);
	}

	private stream(chunk: string, isFinished: boolean) {
		// Once we detect an infinite loop and call finish(), we won't be able to recover
		if (this.hasDetectedInfiniteLoop) {
			return;
		}
		const results = this.parser.parse(chunk);

		for (const result of results) {
			if (result instanceof InitAction) {
				if (result.localId) {
					if (
						this.lastAction === `${result.name}-${result.localId}` &&
						!SKIP_INFINITE_LOOP_ACTIONS.includes(result.name)
					) {
						// Once we detect an infinite loop, we keep skipping and wait for the streaming to finish.
						this.hasDetectedInfiniteLoop = true;
						this.finish();
						fireInfiniteLoopDetectedAnalyticsEvent(this.editorApi, result);
					} else {
						this.hasDetectedInfiniteLoop = false;
						this.currentToolCall = this.createToolCallProcessor(result);
					}
					this.lastAction = `${result.name}-${result.localId}`;
				} else {
					this.lastAction = null;
					this.currentToolCall = this.createToolCallProcessor(result);
				}
			} else if (result instanceof StreamAction) {
				this.currentToolCall?.stream(result.partialValue, isFinished);
			} else if (result instanceof FinishAction) {
				this.currentToolCall?.finish();
			} else {
				throw new Error('Unknown action type from partial stream parser');
			}
		}
	}

	async finish(): Promise<void> {
		if (this.hasDetectedInfiniteLoop) {
			this.rovoBridgeAPI.send(InlineInputBoxAbortMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				senderId: this.clientId,
			});
			this.reset();
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
		} else {
			this.accumulated = '';
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();

			if (this.hasDetectedInfiniteLoop) {
				this.rovoBridgeAPI.send(InlineInputBoxAbortMessageCommand, {
					receiverId: ROVO_CHAT_CLIENT_ID,
					senderId: this.clientId,
				});
			}

			this.lastAction = null;
			this.currentToolCall = undefined;
			this.parser.reset();
		}
	}

	abort(): void {
		this.reset();
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}
}
